// =============================================================================
// LineItemRow.tsx
// Single row of the MobileAddOrder line-items editor. Row 1: item name + an
// optional "remove" button (only when the form has more than one row). Row 2:
// Qty / Price / Size / SKU. Numeric column widths are bumped to fit the 16px
// iOS-zoom-fix font size.
// =============================================================================

import React from 'react';

// ── TYPES ────────────────────────────────────────────────────────────

export interface FormItem {
  item_name: string;
  quantity: number;
  unit_price: number;
  size: string;
  sku: string;
}

interface LineItemRowProps {
  item: FormItem;
  index: number;
  total: number;
  onChange: (idx: number, field: keyof FormItem, value: string | number) => void;
  onRemove: (idx: number) => void;
}

// ── CONSTANTS ────────────────────────────────────────────────────────

const numCellQty:  React.CSSProperties = { width: 64, textAlign: 'center', flexShrink: 0, background: 'var(--surface)' };
const numCellSku:  React.CSSProperties = { width: 76,                       flexShrink: 0, background: 'var(--surface)' };
const numCellSize: React.CSSProperties = { width: 76,                       flexShrink: 0, background: 'var(--surface)' };

// ── MAIN COMPONENT ───────────────────────────────────────────────────

export function LineItemRow({ item, index, total, onChange, onRemove }: LineItemRowProps) {
  return (
    <div style={{ marginBottom: index < total - 1 ? 10 : 0 }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
        <input
          value={item.item_name}
          onChange={e => onChange(index, 'item_name', e.target.value)}
          placeholder="Item name"
          className="input"
          style={{ flex: 1, background: 'var(--surface)' }}
        />
        {total > 1 && (
          <button
            onClick={() => onRemove(index)}
            aria-label="Remove item"
            style={{
              width: 36, minWidth: 36, height: 'auto',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0,
            }}
          >
            <svg viewBox="0 0 16 16" style={{ width: 14, height: 14, fill: 'none', stroke: 'var(--red)', strokeWidth: 1.5 }}>
              <path d="M3 8h10" />
            </svg>
          </button>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          type="number"
          value={item.quantity || ''}
          onChange={e => onChange(index, 'quantity', parseInt(e.target.value) || 0)}
          placeholder="Qty"
          min={1}
          className="input"
          style={numCellQty}
        />
        <input
          type="number"
          value={item.unit_price || ''}
          onChange={e => onChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
          placeholder="Price"
          step="0.01"
          min={0}
          className="input"
          style={{ flex: 1, textAlign: 'right' as const, background: 'var(--surface)' }}
        />
        <input
          value={item.size}
          onChange={e => onChange(index, 'size', e.target.value)}
          placeholder="Size"
          className="input"
          style={numCellSize}
        />
        <input
          value={item.sku}
          onChange={e => onChange(index, 'sku', e.target.value)}
          placeholder="SKU"
          className="input"
          style={numCellSku}
        />
      </div>
    </div>
  );
}
